import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  importCourseRequests,
  listCourseRequests,
} from "./course-requests-api";
import { ISSUES_KEY } from "./use-issues";
import {
  SCHEDULE_EVENTS_BASE_KEY,
  SCHEDULE_KEY,
  SCHEDULES_KEY,
} from "./use-schedules";

export const COURSE_REQUESTS_KEY = (scheduleId: string) =>
  ["course-requests", scheduleId] as const;

export function useCourseRequests(
  scheduleId: string,
  options: { enabled?: boolean } = {},
) {
  const enabledGate = options.enabled ?? true;
  return useQuery({
    queryKey: COURSE_REQUESTS_KEY(scheduleId),
    queryFn: () => listCourseRequests(scheduleId),
    enabled: enabledGate && !!scheduleId,
  });
}

/**
 * Importing course requests is a multi-cache side effect:
 *  - `course-requests` list itself changes;
 *  - `schedule-issues` may add/remove rows;
 *  - the schedule header (`issueCounts`) may flip;
 *  - the parent schedule list also surfaces `issueCounts`;
 *  - calendar events render error chips driven by issue rows.
 * See docs/solutions/integration-issues/tanstack-cross-cache-invalidation-after-side-effects.md.
 */
export interface ImportCourseRequestsVariables {
  file: File;
  replaceAll: boolean;
  /** Optional abort signal so the caller can cancel an in-flight upload. */
  signal?: AbortSignal;
}

export function useImportCourseRequests(scheduleId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ file, replaceAll, signal }: ImportCourseRequestsVariables) =>
      importCourseRequests(scheduleId, file, replaceAll, signal),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: COURSE_REQUESTS_KEY(scheduleId) });
      qc.invalidateQueries({ queryKey: ISSUES_KEY(scheduleId) });
      qc.invalidateQueries({ queryKey: SCHEDULE_KEY(scheduleId) });
      qc.invalidateQueries({ queryKey: SCHEDULES_KEY });
      qc.invalidateQueries({
        queryKey: SCHEDULE_EVENTS_BASE_KEY(scheduleId),
      });
    },
  });
}
